import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';

import type { LeadFilters } from '../api/endpoints';
import { api } from '../api/endpoints';
import type { LeadSummary } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { ErrorMessage } from '../components/ErrorMessage';
import { GeoStatusBadge } from '../components/GeoStatusBadge';
import { PageLoader } from '../components/Loader';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { filterKeys, hasActiveFilters, readFilters } from './leads/filters';
import { LeadFilterBar } from './leads/LeadFilters';

const dateTime = new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' });

function displayName(lead: LeadSummary): string {
  const person = [lead.first_name, lead.last_name].filter(Boolean).join(' ');
  return lead.company_name ? (person ? `${lead.company_name} (${person})` : lead.company_name) : person || '–';
}

// Leadliste mit serverseitiger Suche, Filterung, Sortierung und Cursor-Pagination.
// Die Filter stehen in der URL (verlinkbar, Zurück-Taste funktioniert).
// Makler-Sicht (OFFENE_FRAGEN B-28): mode own = „Mit Termin“ (fixed appointment_user_id=me – Leads, bei denen der Makler
// Termine hatte oder hat, nicht alle Leads seines Gebiets), mode pool = Bestand eines Regelgebiets (fixed Zuständigkeit und
// Gebiet). Filter, die die Ansicht nicht anbietet, werden aus der URL nicht übernommen.
const hiddenKeys: Record<'all' | 'own' | 'pool', (keyof LeadFilters)[]> = {
  all: [],
  own: ['geo_status', 'federal_state_id', 'county_id', 'area_id', 'assigned_user_id', 'assigned_field_sales_user_id'],
  pool: ['geo_status', 'area_id', 'assigned_user_id', 'assigned_field_sales_user_id'],
};

export function LeadsPage({
  embedded = false,
  mode = 'all',
  fixed,
  emptyText,
}: {
  embedded?: boolean;
  mode?: 'all' | 'own' | 'pool';
  fixed?: LeadFilters;
  emptyText?: string;
}) {
  const { project } = useProject();
  const papi = useProjectApi();
  const [params, setParams] = useSearchParams();
  const own = mode !== 'all';
  const userFilters = useMemo(() => {
    const f = readFilters(params);
    for (const key of hiddenKeys[mode]) delete f[key];
    return f;
  }, [params, mode]);
  const filters = useMemo(() => ({ ...userFilters, ...fixed }), [userFilters, fixed]);
  const userFiltered = hasActiveFilters(userFilters);

  const statuses = useQuery({
    queryKey: ['lead-statuses'],
    queryFn: ({ signal }) => api.leadStatuses(signal),
    staleTime: 60 * 60_000,
  });
  const leads = useInfiniteQuery({
    queryKey: projectKeys.leads(project.id, filters),
    queryFn: ({ pageParam, signal }) => papi.leads({ ...filters, cursor: pageParam, limit: 50 }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const update = useCallback(
    (patch: Partial<LeadFilters>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            next.delete(key);
            for (const v of Array.isArray(value) ? value : value === undefined ? [] : [value]) next.append(key, String(v));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const reset = useCallback(() => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const key of filterKeys) if (key !== 'sort' && key !== 'order') next.delete(key);
      return next;
    });
  }, [setParams]);

  const labels = new Map((statuses.data?.items ?? []).map((s) => [s.code, s.label]));
  const items = leads.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className={embedded ? 'embedded-page' : 'page'}>
      <header className="page-header">
        {!embedded && <h1>Leads</h1>}
        {leads.data && (
          <span className="muted">
            {items.length}
            {leads.hasNextPage ? '+' : ''} angezeigt
          </span>
        )}
      </header>
      <LeadFilterBar filters={filters} statuses={statuses.data?.items ?? []} onChange={update} onReset={reset} mode={mode} active={userFiltered} />
      {leads.isPending ? (
        <PageLoader label="Leads werden geladen …" />
      ) : leads.isError ? (
        <ErrorMessage error={leads.error} onRetry={() => void leads.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          title={userFiltered ? 'Keine Treffer' : 'Noch keine Leads'}
          icon={userFiltered ? 'help' : 'user'}
          action={
            userFiltered && (
              <button type="button" className="btn" onClick={reset}>
                Filter zurücksetzen
              </button>
            )
          }
        >
          {userFiltered
            ? 'Für diese Filter wurden keine Leads gefunden – Filter lockern oder zurücksetzen.'
            : (emptyText ?? 'In diesem Projekt sind noch keine Leads vorhanden. Leads kommen über Importe und Schnittstellen.')}
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Adresse</th>
                <th scope="col">Status</th>
                {!own && <th scope="col">Geo</th>}
                <th scope="col">Telefon</th>
                <th scope="col">Nächster Kontakt</th>
              </tr>
            </thead>
            <tbody>
              {items.map((lead) => (
                <tr key={lead.id}>
                  <td>
                    <Link to={`/p/${project.key}/leads/${lead.id}`}>{displayName(lead)}</Link>
                  </td>
                  <td>
                    <div>{[lead.postal_code, lead.city].filter(Boolean).join(' ') || '–'}</div>
                    {lead.street && (
                      <small className="muted">{[lead.street, lead.house_number].filter(Boolean).join(' ')}</small>
                    )}
                  </td>
                  <td>
                    <span className="badge">{labels.get(lead.status) ?? lead.status}</span>
                  </td>
                  {!own && (
                    <td>
                      <GeoStatusBadge status={lead.geo_status} />
                    </td>
                  )}
                  <td>{lead.phone ?? '–'}</td>
                  <td>{lead.next_contact_at ? dateTime.format(new Date(lead.next_contact_at)) : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {leads.hasNextPage && (
        <button
          type="button"
          className="btn load-more"
          onClick={() => void leads.fetchNextPage()}
          disabled={leads.isFetchingNextPage}
        >
          {leads.isFetchingNextPage ? 'Lädt …' : 'Weitere laden'}
        </button>
      )}
    </div>
  );
}
